import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import authService, { UserProfile } from '../../services/auth.service';
import adhesionsService, { Adhesion } from '../../services/adhesions.service';
import { toast } from 'react-toastify';

const AdherentDashboard: React.FC = () => {
    const [user, setUser] = useState<UserProfile | null>(null);
    const [adhesion, setAdhesion] = useState<Adhesion | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const profile = authService.getUserProfile();
                if (profile) {
                    setUser(profile);
                }

                const response = await adhesionsService.getMyAdhesion();
                if (response?.data) {
                    setAdhesion(response.data);
                }
            } catch (err: any) {
                console.error('Error fetching adhesion:', err);
                toast.error('Erreur lors de la récupération des informations');
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    if (loading) {
        return (
            <div className="flex h-full items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary"></div>
            </div>
        );
    }

    if (!adhesion || !user) {
        return (
            <div className="flex h-full items-center justify-center text-gray-500">
                Impossible de charger vos informations.
            </div>
        );
    }

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                staggerChildren: 0.1
            }
        }
    };

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
        >
            {/* Beautiful Welcome Section, no hard borders, no gray background */}
            <div className="bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-secondary-dark to-secondary">
                        Bienvenue, {adhesion.prenom} {adhesion.nom}!
                    </h1>
                    <p className="text-gray-500 mt-2 text-lg">
                        Heureux de vous revoir dans votre espace adhérent SAPI.
                    </p>
                </div>
                <div className="hidden md:block">
                    <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-emerald-50 text-emerald-600 shadow-sm">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2 animate-pulse"></span>
                        {adhesion.statut === 'validated' ? 'Membre Actif' : 'Membre'}
                    </span>
                </div>
            </div>

            {/* Empty Main Dashboard Area as requested */}
            <div className="bg-white rounded-2xl p-8 min-h-[400px] flex items-center justify-center border border-gray-50 shadow-[0_4px_20px_rgb(0,0,0,0.02)]">
                <div className="text-center max-w-md">
                    <h2 className="text-xl font-medium text-gray-700 mb-2">Votre Tableau de Bord</h2>
                    <p className="text-gray-400">
                        Utilisez le menu pour consulter votre profil ou gérer vos adhésions.
                    </p>
                </div>
            </div>
        </motion.div>
    );
};

export default AdherentDashboard;
